import React, { ReactNode, useEffect, useRef } from 'react';
import styles from './ProgrammaticallyMobileScroller.module.scss';

interface ComponentProps {
  height: number;
  children: ReactNode;
}

const ProgrammaticallyMobileScroller = ({ height, children }: ComponentProps) => {
  const scrollerParent = useRef<HTMLDivElement>(null);
  const scrollStick = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const thumbHeightRef = useRef(0);

  useEffect(() => {
    if (scrollerParent.current && scrollStick.current) {
      const containerHeight = scrollerParent.current.clientHeight;
      const contentHeight = scrollerParent.current.scrollHeight;
      const thumbHeightCalculated = (containerHeight / contentHeight) * containerHeight;
      scrollStick.current.style.height = `${thumbHeightCalculated}px`;
      thumbHeightRef.current = thumbHeightCalculated;

      window.requestAnimationFrame(() => {
        if (
          scrollerParent.current &&
          scrollStick.current &&
          scrollerParent.current.firstElementChild
        ) {
          if (scrollerParent.current.firstElementChild.offsetHeight < height) {
            scrollStick.current.style.height = `0px`;
          }
        }
      });
    }
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    startYRef.current = e.touches[0].pageY;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (!scrollerParent.current || !scrollStick.current) return;

    const currentY = e.touches[0].pageY;
    const diffY = startYRef.current - currentY;
    scrollerParent.current.scrollTop += diffY;

    const scrollerElement = scrollerParent.current;
    const containerHeight = scrollerElement.clientHeight;
    const scrollableDistance = scrollerElement.scrollHeight - containerHeight;
    const scrollTop = scrollerElement.scrollTop;
    const thumbMovementArea = containerHeight - thumbHeightRef.current;
    const newThumbTop = (scrollTop / scrollableDistance) * thumbMovementArea;

    window.requestAnimationFrame(() => {
      scrollStick.current!.style.top = `${newThumbTop}px`;
    });

    startYRef.current = currentY;
  };

  return (
    <div style={{ position: 'relative', maxHeight: height }}>
      <div ref={scrollStick} className={styles.scrollStick}></div>
      <div
        style={{ overflowY: 'auto', maxHeight: height, position: 'relative' }}
        ref={scrollerParent}
        className={styles.scrollParent}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      >
        {children}
      </div>
    </div>
  );
};

export default ProgrammaticallyMobileScroller;
